import type { LucideIcon } from "lucide-react";
import { createElement, type ReactNode } from "react";
import { cn } from "@/lib/utils";

export interface TituloSeccionProps {
  /** `seccion`: h2 de 16px. `ficha`: h2 de 18px (orden, cliente). `etiqueta`: eyebrow. */
  nivel?: "seccion" | "ficha" | "etiqueta";
  /** Default: seccion/ficha → h2, etiqueta → h3. */
  as?: "h2" | "h3" | "span";
  icono?: LucideIcon;
  /** Debajo del titulo, en gris. */
  descripcion?: string;
  /** A la derecha del titulo, tabular. */
  contador?: number | string;
  /** A la derecha, alineado por linea base. */
  acciones?: ReactNode;
  className?: string;
  children: ReactNode;
}

const NIVELES = {
  seccion: "text-base font-semibold [&_svg]:size-5",
  ficha: "text-lg font-semibold tracking-tight",
  etiqueta: "text-xs font-medium uppercase tracking-wide text-muted-foreground [&_svg]:size-3.5",
} as const;

/**
 * El unico titulo de seccion. Convierte en headings reales los eyebrows que
 * hoy son <span> y unifica las cabeceras "titulo a la izquierda, accion a la
 * derecha" que cada pantalla arma a mano. El h1 es de `PageHeader`.
 */
export function TituloSeccion({
  nivel = "seccion",
  as,
  icono: Icono,
  descripcion,
  contador,
  acciones,
  className,
  children,
}: TituloSeccionProps): JSX.Element {
  const etiqueta = as ?? (nivel === "etiqueta" ? "h3" : "h2");

  const titulo = createElement(
    etiqueta,
    {
      className: cn(
        "flex min-w-0 items-center gap-2 [&_svg]:shrink-0",
        NIVELES[nivel],
        !acciones && !descripcion && contador === undefined && className,
      ),
    },
    Icono ? <Icono aria-hidden="true" /> : null,
    <span className="min-w-0">{children}</span>,
    contador !== undefined ? (
      <span className="text-xs font-normal normal-case tabular-nums tracking-normal text-muted-foreground">
        {contador}
      </span>
    ) : null,
  );

  // Sin nada alrededor, el heading solo: asi entra en cualquier flex sin
  // envoltorio extra.
  if (!acciones && !descripcion && contador === undefined) return titulo;

  return (
    <div className={cn("flex flex-wrap items-baseline justify-between gap-2", className)}>
      <div className="min-w-0">
        {titulo}
        {descripcion ? <p className="text-sm text-muted-foreground">{descripcion}</p> : null}
      </div>
      {acciones ? (
        // `max-w-full`: sin tope, unas acciones largas no se achicaban y en el
        // celular se salían por la derecha (la leyenda de la ficha del cliente).
        <div className="flex max-w-full shrink-0 flex-wrap items-center gap-2">{acciones}</div>
      ) : null}
    </div>
  );
}
